/**
 * ProgressSyncService
 * Syncs roadmap task progress with Supabase for cross-device access
 */

import { supabase } from '../lib/supabase';

const SYNC_DEBOUNCE_MS = 500;
const LOCAL_STORAGE_KEY = 'fixarr-roadmap-state-v6';
const CHECKLIST_KEY = 'fixarr-roadmap-checklist-v1';

class ProgressSyncService {
    constructor() {
        this.saveTimeout = null;
        this.pendingSaves = new Map();
    }

    /**
     * Load all task progress from Supabase
     */
    async loadProgress(userId) {
        if (!userId) return null;

        try {
            const { data, error } = await supabase
                .from('task_progress')
                .select('task_id, status, checklist_state')
                .eq('user_id', userId);

            if (error) {
                console.error('Error loading progress:', error);
                return null;
            }

            // Convert to the format used by RoadmapDinamico
            const taskStates = [];
            const checklistStates = {};

            data?.forEach(row => {
                taskStates.push({
                    id: row.task_id,
                    status: row.status
                });
                if (row.checklist_state && Object.keys(row.checklist_state).length > 0) {
                    checklistStates[row.task_id] = row.checklist_state;
                }
            });

            return { taskStates, checklistStates };
        } catch (err) {
            console.error('Failed to load progress:', err);
            return null;
        }
    }

    /**
     * Save a single task's progress (debounced)
     */
    saveTaskProgress(userId, taskId, status, checklistState = {}) {
        if (!userId || !taskId) return;

        // Add to pending saves
        this.pendingSaves.set(taskId, { userId, status, checklistState });

        // Debounce the actual save
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }

        this.saveTimeout = setTimeout(() => {
            this._flushPendingSaves();
        }, SYNC_DEBOUNCE_MS);
    }

    /**
     * Flush all pending saves to Supabase
     */
    async _flushPendingSaves() {
        if (this.pendingSaves.size === 0) return;

        const saves = Array.from(this.pendingSaves.entries());
        this.pendingSaves.clear();

        try {
            // Upsert all pending saves
            const records = saves.map(([taskId, data]) => ({
                user_id: data.userId,
                task_id: taskId,
                status: data.status,
                checklist_state: data.checklistState
            }));

            const { error } = await supabase
                .from('task_progress')
                .upsert(records, {
                    onConflict: 'user_id,task_id',
                    ignoreDuplicates: false
                });

            if (error) {
                console.error('Error saving progress:', error);
            }
        } catch (err) {
            console.error('Failed to save progress:', err);
        }
    }

    /**
     * Migrate local storage to Supabase (first time sync)
     */
    async migrateLocalToRemote(userId) {
        if (!userId) return false;

        try {
            // Load from localStorage
            const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
            const savedChecklist = localStorage.getItem(CHECKLIST_KEY);

            const taskStates = savedState ? JSON.parse(savedState) : [];
            const checklistStates = savedChecklist ? JSON.parse(savedChecklist) : {};

            if (taskStates.length === 0 && Object.keys(checklistStates).length === 0) {
                return true; // Nothing to migrate
            }

            // Build records to upsert
            const records = [];
            const processedTasks = new Set();

            // Add task states
            taskStates.forEach(task => {
                if (task.id) {
                    records.push({
                        user_id: userId,
                        task_id: task.id,
                        status: task.status || 'pending',
                        checklist_state: checklistStates[task.id] || {}
                    });
                    processedTasks.add(task.id);
                }
            });

            // Add any checklist-only entries
            Object.keys(checklistStates).forEach(taskId => {
                if (!processedTasks.has(taskId)) {
                    records.push({
                        user_id: userId,
                        task_id: taskId,
                        status: 'pending',
                        checklist_state: checklistStates[taskId]
                    });
                }
            });

            if (records.length === 0) return true;

            // Upsert to Supabase
            const { error } = await supabase
                .from('task_progress')
                .upsert(records, {
                    onConflict: 'user_id,task_id',
                    ignoreDuplicates: false
                });

            if (error) {
                console.error('Migration error:', error);
                return false;
            }

            console.log(`Migrated ${records.length} tasks to Supabase`);
            return true;
        } catch (err) {
            console.error('Migration failed:', err);
            return false;
        }
    }

    /**
     * Check if user has any progress in Supabase
     */
    async hasRemoteProgress(userId) {
        if (!userId) return false;

        try {
            const { count, error } = await supabase
                .from('task_progress')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', userId);

            if (error) return false;
            return (count || 0) > 0;
        } catch {
            return false;
        }
    }

    /**
     * Force immediate save (call on page unload)
     */
    async forceFlush() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        await this._flushPendingSaves();
    }
}

export const progressSyncService = new ProgressSyncService();
export default progressSyncService;
